/**
 * Banco de horas (E7b) — extrato mensal por pessoa com saldo acumulado derivado
 * dos movimentos (nunca armazenado como verdade — módulo 5 §4.2). Padrão Mantine.
 */
import { useEffect, useState } from "react";
import { ActionIcon, Alert, Badge, Button, Card, Group, Select, Table, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { api } from "../api";
import { fmtHhMm, fmtCompetencia, fmtData, fmtDataHora } from "../format";
import { CompetenciaField, HhMmField } from "../componentes/entradas-mantine";
import { Confirmacao, notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { Carregando, Vazio } from "../componentes/Estados";
import { TabelaDados, Th } from "../componentes/listas";

interface Funcionario {
  id: string;
  nome: string;
}
interface Movimento {
  id: string;
  tipo: string;
  minutos: number;
  descricao: string | null;
  criadoEm: string;
  pontoImportacaoId?: string | null;
  lancamentoId?: string | null;
}
interface Extrato {
  funcionario: Funcionario;
  parametros: { capDiarioMinutos: number; prazoCompensacaoMeses: number; pagaSaldoDesligamento: boolean };
  /** Saldo de cabeçalho: ANCORADO no último espelho do TT quando há snapshot
   *  (23/09/2026); o extrato por mês segue ledger puro (trilha de auditoria). */
  saldoAtual: number;
  saldoFonte?: { competenciaId: number; dataFimIso: string } | null;
  meses: { competencia: number; movimentos: Movimento[]; saldoApos: number }[];
}

/** Resumo geral de saldos de todos os colaboradores (24/09/2026). */
interface ResumoGeral {
  total: number;
  resumo: { funcionarioId: string; nome: string; matriculaDominio: number | null; saldoMinutos: number }[];
}

const ROTULO_TIPO: Record<string, string> = {
  gerada: "Gerada (+)",
  compensada: "Compensada (−)",
  emenda: "Emenda (−)",
  saida_parcial: "Saída parcial (−)",
  ajuste: "Ajuste (±)",
};

export function BancoHorasPage({ funcionarioId }: { funcionarioId?: string }) {
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [selecionado, setSelecionado] = useState(funcionarioId ?? "");
  const [extrato, setExtrato] = useState<Extrato | null>(null);
  const [resumo, setResumo] = useState<ResumoGeral | null>(null);
  const [resumoAberto, setResumoAberto] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [recarregar, setRecarregar] = useState(0);
  const [fComp, setFComp] = useState<number | null>(202607);
  const [fTipo, setFTipo] = useState("gerada");
  const [fMin, setFMin] = useState<number | null>(null);
  const [fDesc, setFDesc] = useState("");
  const [confirmacao, setConfirmacao] = useState<{ titulo: string; consequencia: string; rotuloConfirmar: string; acao: () => void } | null>(null);

  /** 24/09/2026 (pedido do dono) — excluir movimento MANUAL (importados e os
   *  ligados a lançamento da folha não têm lixeira: saem pela importação ou
   *  pelo estorno do pagamento). */
  function pedirExclusaoMovimento(mv: Movimento) {
    setConfirmacao({
      titulo: "Excluir este movimento?",
      consequencia: `O movimento ${ROTULO_TIPO[mv.tipo] ?? mv.tipo} de ${fmtHhMm(mv.minutos)} some do extrato e o saldo é recalculado na hora. Esta ação fica registrada na auditoria.`,
      rotuloConfirmar: "Excluir",
      acao: () => void excluirMovimento(mv),
    });
  }
  async function excluirMovimento(mv: Movimento) {
    try {
      setErro(null);
      await api.del(`/funcionarios/${selecionado}/banco-horas/${mv.id}`);
      notificarOk("Movimento excluído.");
      setRecarregar((x) => x + 1);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  useEffect(() => {
    api
      .get<{ funcionarios: Funcionario[] }>("/funcionarios?tipoVinculo=clt&ativo=true")
      .then((r) => {
        // 24/09/2026 — o combo mostra os nomes em ordem alfabética (pt-BR)
        const ordenados = [...r.funcionarios].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
        setFuncionarios(ordenados);
        if (!selecionado && ordenados.length > 0) setSelecionado(ordenados[0]!.id);
      })
      .catch(setErro);
    // 24/09/2026 — resumo geral de saldos de todos os colaboradores
    api.get<ResumoGeral>("/banco-horas/resumo").then(setResumo).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selecionado) return;
    api
      .get<Extrato>(`/funcionarios/${selecionado}/banco-horas`)
      .then(setExtrato)
      .catch(setErro);
  }, [selecionado, recarregar]);

  async function novoMovimento() {
    if (fComp === null || fMin === null) return;
    try {
      setErro(null);
      await api.post(`/funcionarios/${selecionado}/banco-horas`, {
        competenciaId: fComp,
        tipo: fTipo,
        minutos: fMin,
        descricao: fDesc || undefined,
      });
      notificarOk("Movimento registrado.");
      setFMin(null);
      setFDesc("");
      setRecarregar((x) => x + 1);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  return (
    <>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Title order={2}>Banco de horas</Title>
        <Group>
          <Select
            aria-label="Funcionário"
            data={funcionarios.map((f) => ({ value: f.id, label: f.nome }))}
            value={selecionado || null}
            onChange={(v) => setSelecionado(v ?? "")}
            w={300}
            searchable
            nothingFoundMessage="Ninguém com esse nome"
          />
          {extrato && (
            <Badge
              size="xl"
              variant="light"
              color={extrato.saldoAtual < 0 ? "red" : "sillis"}
              title={
                extrato.saldoFonte
                  ? `âncora: espelho do Tique Taque de ${fmtData(extrato.saldoFonte.dataFimIso)} + movimentos da folha posteriores`
                  : undefined
              }
            >
              Saldo: {fmtHhMm(extrato.saldoAtual)}
            </Badge>
          )}
        </Group>
      </Group>
      {erro != null && (
        <Alert color="red" title="Falha ao carregar" mb="sm">
          {String(erro)}
        </Alert>
      )}
      {extrato && (
        <Text size="xs" c="dimmed" mb="sm">
          Parâmetros: cap {fmtHhMm(extrato.parametros.capDiarioMinutos)}/dia · compensação em{" "}
          {extrato.parametros.prazoCompensacaoMeses} meses (default documentado — a confirmar com o dono) · saldo em
          desligamento {extrato.parametros.pagaSaldoDesligamento ? "pago (CLT art. 59 §3º — M4.2)" : "não pago (vigência antiga — diverge do art. 59 §3º)"}
        </Text>
      )}

      {resumo && (
        <Card withBorder shadow="xs" mb="md" data-testid="resumo-geral">
          <Group justify="space-between" mb={resumoAberto ? "xs" : 0}>
            <Title order={5}>Resumo geral de saldos ({resumo.total})</Title>
            <Button size="compact-xs" variant="subtle" onClick={() => setResumoAberto((v) => !v)}>
              {resumoAberto ? "Ocultar" : "Mostrar"}
            </Button>
          </Group>
          {resumoAberto && (
            <TabelaDados>
              <Table.Thead>
                <Table.Tr>
                  <Th>Pessoa</Th>
                  <Th num>Matr.</Th>
                  <Th num>Saldo</Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {resumo.resumo.map((r) => (
                  <Table.Tr key={r.funcionarioId}>
                    <Table.Td>{r.nome}</Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text size="xs" c="dimmed">{r.matriculaDominio ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text ff="monospace" size="sm" c={r.saldoMinutos < 0 ? "red" : undefined}>{fmtHhMm(r.saldoMinutos)}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </TabelaDados>
          )}
        </Card>
      )}

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">Novo movimento (ajuste manual pontual)</Title>
        <Group align="flex-end" wrap="wrap">
          <CompetenciaField label="Competência" valor={fComp} onChange={setFComp} />
          <Select
            label="Tipo"
            data={Object.entries(ROTULO_TIPO).map(([value, label]) => ({ value, label }))}
            value={fTipo}
            onChange={(v) => setFTipo(v ?? "gerada")}
          />
          <HhMmField label="Horas" valor={fMin} onChange={setFMin} w={110} />
          <TextInput label="Motivo (opcional)" value={fDesc} onChange={(e) => setFDesc(e.currentTarget.value)} w={280} />
          <Button onClick={() => void novoMovimento()} disabled={fComp === null || fMin === null}>
            Registrar
          </Button>
        </Group>
        <Text size="xs" c="dimmed" mt="sm">
          O grosso dos movimentos vem da importação do CSV do Tique Taque (na página da competência).
        </Text>
      </Card>

      {!extrato ? (
        <Carregando linhas={4} />
      ) : extrato.meses.length === 0 ? (
        <Vazio texto="Sem movimentos registrados para esta pessoa." />
      ) : (
        // 24/09/2026 (pedido do dono) — meses em ordem DECRESCENTE (o mais
        // recente em cima); os movimentos dentro do mês seguem cronológicos
        [...extrato.meses].sort((a, b) => b.competencia - a.competencia).map((m) => (
          <Card key={m.competencia} withBorder shadow="xs" mb="sm">
            <Group justify="space-between" mb="xs">
              <Title order={5}>{fmtCompetencia(m.competencia)}</Title>
              <Badge variant="light">saldo após o mês: {fmtHhMm(m.saldoApos)}</Badge>
            </Group>
            <TabelaDados>
              <Table.Thead>
                <Table.Tr>
                  <Th>Tipo</Th>
                  <Th num>Horas</Th>
                  <Th>Descrição</Th>
                  <Th>Registrado em</Th>
                  <Th>Ações</Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {m.movimentos.map((mv) => {
                  // lixeira só em movimento MANUAL solto (importado = sai pela
                  // importação; ligado a lançamento da folha = estorno no passo 2)
                  const manualSolto = mv.pontoImportacaoId == null && mv.lancamentoId == null;
                  return (
                    <Table.Tr key={mv.id}>
                      <Table.Td>{ROTULO_TIPO[mv.tipo] ?? mv.tipo}</Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <Text ff="monospace" size="sm">{fmtHhMm(mv.minutos)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">{mv.descricao ?? ""}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">{fmtDataHora(mv.criadoEm)}</Text>
                      </Table.Td>
                      <Table.Td>
                        {manualSolto && (
                          <Tooltip label="Excluir movimento manual">
                            <ActionIcon color="red" variant="subtle" size="sm" aria-label="Excluir movimento" onClick={() => pedirExclusaoMovimento(mv)}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </TabelaDados>
          </Card>
        ))
      )}
      {confirmacao && (
        <Confirmacao
          aberta
          titulo={confirmacao.titulo}
          consequencia={confirmacao.consequencia}
          rotuloConfirmar={confirmacao.rotuloConfirmar}
          onConfirmar={() => { const a = confirmacao.acao; setConfirmacao(null); a(); }}
          onCancelar={() => setConfirmacao(null)}
        />
      )}
    </>
  );
}
